import { FunctionComponent } from "react";
import i18n from "i18next";
import { InformationSystemMapProps } from "./InformationSystemMap.types";
import { InformationSystemNode } from "../../services/software/InformationSystemMapping.types";
import "./InformationSystemMap.css";

const NODE_WIDTH = 230;
const NODE_HEIGHT = 86;
const COLUMN_GAP = 260;
const ROW_GAP = 118;
const CANVAS_PADDING = 70;
const LAYER_ORDER = ["application", "gateway", "orchestrator", "engine", "repository", "other"];

interface NodePosition {
    x: number;
    y: number;
}

interface LayerDefinition {
    key: string;
    label: string;
    nodes: InformationSystemNode[];
}

const InformationSystemMap: FunctionComponent<InformationSystemMapProps> = ({ map, loading = false, error, onNodeClick }) => {
    if (loading) {
        return <div className="information-system-map__state">{i18n.t("softwareMapping.loading")}</div>;
    }

    if (error) {
        return <div className="information-system-map__state information-system-map__state--error">{error}</div>;
    }

    if (map.nodes.length === 0) {
        return <div className="information-system-map__state">{i18n.t("softwareMapping.empty")}</div>;
    }

    const layers = getLayers(map.nodes);
    const maxRows = Math.max(...layers.map((layer) => layer.nodes.length), 1);
    const canvasWidth = CANVAS_PADDING * 2 + layers.length * NODE_WIDTH + Math.max(0, layers.length - 1) * (COLUMN_GAP - NODE_WIDTH);
    const canvasHeight = CANVAS_PADDING * 2 + 30 + maxRows * NODE_HEIGHT + Math.max(0, maxRows - 1) * (ROW_GAP - NODE_HEIGHT);
    const positions = getLayeredPositions(layers, maxRows);

    function getLayerKey(node: InformationSystemNode): string {
        const category = node.category?.toLowerCase();
        if (category === "application" || category === "gateway" || category === "orchestrator" || category === "engine" || category === "repository") {
            return category;
        }
        return "other";
    }

    function getLayerLabel(layerKey: string): string {
        const labels: Record<string, string> = {
            application: i18n.t("softwareMapping.layers.application"),
            gateway: i18n.t("softwareMapping.layers.gateway"),
            orchestrator: i18n.t("softwareMapping.layers.orchestrator"),
            engine: i18n.t("softwareMapping.layers.engine"),
            repository: i18n.t("softwareMapping.layers.repository"),
            other: i18n.t("softwareMapping.layers.other"),
        };
        return labels[layerKey] ?? layerKey;
    }

    function getLayers(nodes: InformationSystemNode[]): LayerDefinition[] {
        const groupedNodes = nodes.reduce<Record<string, InformationSystemNode[]>>((accumulator, node) => {
            const layerKey = getLayerKey(node);
            accumulator[layerKey] = [...(accumulator[layerKey] ?? []), node];
            return accumulator;
        }, {});

        return LAYER_ORDER
            .map((layerKey) => ({
                key: layerKey,
                label: getLayerLabel(layerKey),
                nodes: (groupedNodes[layerKey] ?? []).sort((left, right) => left.name.localeCompare(right.name)),
            }))
            .filter((layer) => layer.nodes.length > 0);
    }

    function getLayeredPositions(layerDefinitions: LayerDefinition[], rowCount: number): Record<string, NodePosition> {
        return layerDefinitions.reduce<Record<string, NodePosition>>((accumulator, layer, columnIndex) => {
            const columnHeight = layer.nodes.length * ROW_GAP;
            const verticalOffset = Math.max(0, (rowCount * ROW_GAP - columnHeight) / 2);
            layer.nodes.forEach((node, rowIndex) => {
                accumulator[node.id] = {
                    x: CANVAS_PADDING + columnIndex * COLUMN_GAP,
                    y: CANVAS_PADDING + 30 + verticalOffset + rowIndex * ROW_GAP,
                };
            });
            return accumulator;
        }, {});
    }

    function getNodeAnchor(nodeId: string, side: "left" | "right"): NodePosition | undefined {
        const position = positions[nodeId];
        if (!position) {
            return undefined;
        }
        return {
            x: side === "right" ? position.x + NODE_WIDTH : position.x,
            y: position.y + NODE_HEIGHT / 2,
        };
    }

    function renderLinkPath(sourceNodeId: string, targetNodeId: string): string | undefined {
        const sourcePosition = positions[sourceNodeId];
        const targetPosition = positions[targetNodeId];
        if (!sourcePosition || !targetPosition) {
            return undefined;
        }
        const sourceIsLeftOfTarget = sourcePosition.x <= targetPosition.x;
        const source = getNodeAnchor(sourceNodeId, sourceIsLeftOfTarget ? "right" : "left");
        const target = getNodeAnchor(targetNodeId, sourceIsLeftOfTarget ? "left" : "right");
        if (!source || !target) {
            return undefined;
        }
        const midX = (source.x + target.x) / 2;
        return "M " + source.x + " " + source.y + " L " + midX + " " + source.y + " L " + midX + " " + target.y + " L " + target.x + " " + target.y;
    }

    function renderNode(node: InformationSystemNode) {
        const position = positions[node.id];
        return (
            <foreignObject key={node.id} x={position.x} y={position.y} width={NODE_WIDTH} height={NODE_HEIGHT}>
                <div
                    className={"information-system-map__node-card information-system-map__node-card--" + getLayerKey(node)}
                    role="button"
                    tabIndex={0}
                    onClick={() => onNodeClick?.(node)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            onNodeClick?.(node);
                        }
                    }}
                    title={node.name}
                >
                    <div className="information-system-map__node-title">{node.name}</div>
                    <div className="information-system-map__node-metadata">
                        {node.category && <span className="information-system-map__node-tag">{node.category}</span>}
                        {node.status && <span className="information-system-map__node-tag">{node.status}</span>}
                        {node.version && <span className="information-system-map__node-tag">v{node.version}</span>}
                        {node.endpointLabel && <span className="information-system-map__node-tag">{node.endpointLabel}</span>}
                    </div>
                </div>
            </foreignObject>
        );
    }

    return (
        <section className="information-system-map" aria-label={i18n.t("softwareMapping.ariaLabel")}>
            <div className="information-system-map__canvas-wrapper">
                <svg className="information-system-map__canvas" width={canvasWidth} height={canvasHeight} viewBox={"0 0 " + canvasWidth + " " + canvasHeight} role="img">
                    <defs>
                        <marker id="information-system-map-arrow" markerWidth="10" markerHeight="10" refX="10" refY="3" orient="auto" markerUnits="strokeWidth">
                            <path d="M0,0 L0,6 L9,3 z" fill="#6c757d" />
                        </marker>
                    </defs>
                    {layers.map((layer, index) => (
                        <text key={layer.key} className="information-system-map__layer-label" x={CANVAS_PADDING + index * COLUMN_GAP} y={CANVAS_PADDING - 18}>
                            {layer.label}
                        </text>
                    ))}
                    {map.links.map((link) => {
                        const path = renderLinkPath(link.sourceDeviceId, link.targetDeviceId);
                        if (!path) {
                            return null;
                        }
                        return <path key={link.id} className="information-system-map__link" d={path} markerEnd="url(#information-system-map-arrow)" aria-label={`${link.sourceDeviceId} to ${link.targetDeviceId}`} />;
                    })}
                    {map.nodes.map(renderNode)}
                </svg>
            </div>
        </section>
    );
};

export default InformationSystemMap;
